import React from 'react';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { getTimezoneAbbr } from 'src/utils/format-date-time';

import styles from 'src/components/create-or-update-elise-tour-modal/EliseSlotGrid.module.css';

interface Props {
    selectedDate: dayjs.Dayjs | null;
    onSelectDate: (date: dayjs.Dayjs | null) => void;
    isDateUnavailable: (date: dayjs.Dayjs) => boolean;
    slotsForSelectedDate: string[];
    selectedSlot: string | null;
    onSelectSlot: (slot: string) => void;
    isLoading: boolean;
    isError: boolean;
    availabilityWindowDays: number;
    /** The building's own timezone from Elise (calendarTimeZone) -- slot times are always
     * shown in this zone, not the viewer's browser timezone. */
    eliseTimezone: string | undefined;
}

export default function EliseSlotGrid(props: Props) {
    const {
        selectedDate,
        onSelectDate,
        isDateUnavailable,
        slotsForSelectedDate,
        selectedSlot,
        onSelectSlot,
        isLoading,
        isError,
        availabilityWindowDays,
        eliseTimezone,
    } = props;

    return (
        <>
            {isLoading && (
                <Typography variant="body2" data-testid="elise-availability-loading">
                    Loading availability...
                </Typography>
            )}
            {isError && (
                <Alert severity="warning" className={styles.modalAlert} data-testid="elise-availability-error">
                    Could not load availability from Elise.
                </Alert>
            )}
            {!isLoading && !isError && !selectedDate && (
                <Typography variant="body2" data-testid="elise-no-availability">
                    No available tour times found in the next {availabilityWindowDays} days.
                </Typography>
            )}

            {!isLoading && !isError && selectedDate && (
                <>
                    {eliseTimezone && (
                        <Alert severity="info" className={styles.modalAlert} data-testid="tour-timezone-info">
                            <Typography variant="body2">
                                Your tour will be scheduled in{' '}
                                <strong>{eliseTimezone} ({getTimezoneAbbr(eliseTimezone)})</strong>
                                , the building&apos;s timezone.
                            </Typography>
                        </Alert>
                    )}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div data-testid="elise-date-select">
                            <DatePicker
                                label="Date"
                                value={selectedDate}
                                onChange={onSelectDate}
                                shouldDisableDate={isDateUnavailable}
                                minDate={dayjs()}
                                maxDate={dayjs().add(availabilityWindowDays, 'day')}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: false,
                                    },
                                }}
                            />
                        </div>
                    </LocalizationProvider>

                    <div className={styles.slotGrid} data-testid="elise-slot-grid">
                        {slotsForSelectedDate.map((slot) => (
                            <Button
                                key={slot}
                                variant={selectedSlot === slot ? 'contained' : 'outlined'}
                                onClick={() => onSelectSlot(slot)}
                                size="small"
                                data-testid={`elise-slot-${slot}`}
                            >
                                {eliseTimezone ? dayjs(slot).tz(eliseTimezone).format('h:mm A') : dayjs(slot).format('h:mm A')}
                            </Button>
                        ))}
                    </div>
                </>
            )}
        </>
    );
}
